/**
 * Inline viewport-shell styles for {@link AppModal}.
 *
 * Why inline (not only CSS modules / appModalViewportShell.css)?
 * -------------------------------------------------------------
 * Stylesheet order is fragile under Vite's import graph: Mantine's
 * monolith can land after CSS modules and undo `display: flex` /
 * height caps (PBI #1144 sticky-footer flicker). Inline styles on
 * Modal.Content / Modal.Body beat every stylesheet, so every future
 * `<AppModal>` gets a cascade-proof sticky footer without per-modal CSS.
 *
 * `src/styles/appModalViewportShell.css` remains as defense-in-depth
 * (layered vh/dvh/svh when vars are unavailable). Prefer this builder
 * as the primary guarantee.
 *
 * Height uses `--app-svh` from `style.css` (progressive 100vh → 100dvh →
 * 100svh). Sized (non-fullscreen) fill subtracts Mantine's Modal.Inner
 * y-offset the same way AppModal.module.css does.
 */

import type { CSSProperties } from 'react';

/** Matches {@link import('./AppModal').AppModalBodyHeight}. */
export type AppModalViewportBodyHeight = 'fill' | 'auto';

const APP_SVH = 'var(--app-svh, 100vh)';
const SIZED_FILL_HEIGHT = `calc(${APP_SVH} - var(--modal-y-offset, 5dvh) * 2)`;

export type AppModalViewportShellStyles = {
  readonly content: CSSProperties;
  readonly body: CSSProperties;
};

/**
 * Builds content/body style objects for the AppModal flex shell.
 *
 * @param bodyHeight Effective body-height mode (`auto` is coerced to
 *   `fill` by AppModal whenever the modal is fullScreen).
 * @param fullScreen Whether Mantine renders the modal fullScreen.
 */
export function buildAppModalViewportShellStyles(
  bodyHeight: AppModalViewportBodyHeight,
  fullScreen: boolean,
): AppModalViewportShellStyles {
  const body: CSSProperties = {
    display: 'flex',
    flexDirection: 'column',
    flex: '1 1 auto',
    minHeight: 0,
    overflow: 'hidden',
    padding: 0,
  };

  if (bodyHeight === 'auto') {
    return {
      content: {
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        maxHeight: SIZED_FILL_HEIGHT,
      },
      body,
    };
  }

  // fill
  if (fullScreen) {
    return {
      content: {
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        height: APP_SVH,
        maxHeight: APP_SVH,
        // Keep Mantine's var()-based height rules pointing at the same cap.
        ['--modal-content-height' as string]: APP_SVH,
        ['--modal-content-max-height' as string]: APP_SVH,
      },
      body,
    };
  }

  return {
    content: {
      display: 'flex',
      flexDirection: 'column',
      overflow: 'hidden',
      height: SIZED_FILL_HEIGHT,
      maxHeight: SIZED_FILL_HEIGHT,
      ['--modal-content-height' as string]: SIZED_FILL_HEIGHT,
      ['--modal-content-max-height' as string]: SIZED_FILL_HEIGHT,
    },
    body,
  };
}
